import { ref } from 'vue'

import { GROUP_TREE_PATH } from '@/libs/group'
import { parseError } from '@/libs/error'
import i18n from '@/libs/i18n'
import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import { useScopeStore } from '@/stores/scope'
import type { IGroupTree } from '@/types/Group'

/**
 * The one read of `GET /group/tree` the console makes, and every re-read after a change.
 *
 * # Why it is module state
 *
 * Three places want it: the shell on load, the top bar's scope selector, and the
 * „Struktura“ screen after every write. A read per caller would answer the same question
 * three times on the same page load and — worse — would let the selector and the screen
 * disagree about which companies exist. `failed` is module state for the same reason:
 * the top bar has to say that the group could not be read, and the screen has to draw
 * its own refusal from the same fact.
 *
 * The answer lands in the scope store, which is also where a selection that no longer
 * means anything is dropped (`setTree` → `reconcile`).
 */

const loading = ref(false)
const failed  = ref('')

/** The read in flight, so the shell and a screen mounted at the same moment share one. */
let inFlight: Promise<void> | null = null

export const useGroupTree = () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()
    const scopeStore = useScopeStore()

    const read = async (): Promise<void> => {
        loading.value = true

        const { statusCode, data } = await fetchStore.fetch(GROUP_TREE_PATH).get().json<IGroupTree>()

        if (statusCode.value === 200 && data.value) {
            failed.value = ''
            scopeStore.setTree(data.value)
        } else {
            // Said in the service's own words, and NOT as a toast: the group failing to
            // load is the reason the scope selector is empty, and a message that is
            // gone in five seconds leaves an empty selector looking like a group with
            // no houses in it.
            failed.value = parseError(data.value, i18n.global.t('errors.on_load'))
        }

        loading.value = false
    }

    /**
     * Reads the group. Callers that ask at the same moment share one request; a caller
     * that asks later gets a fresh one, which is what makes this the same function the
     * „Struktura“ screen calls after every write.
     */
    const loadTree = (): Promise<void> => {
        if (!appStore.isLoggedIn()) return Promise.resolve()

        inFlight ??= read().finally(() => { inFlight = null })

        return inFlight
    }

    return { loading, failed, loadTree }
}
